import React, { useState, useEffect } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Edit, X, ArrowUp, ArrowDown, Save } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  SET_LINKED_ADDITIONAL_COMPONENTS,
  SEARCH_ADDITIONAL_COMPONENTS,
} from '@/components/queries/additionalComponentQueries'

// --- Types ---

type LinkType = 'INCLUDES' | 'RECOMMENDS' | 'EXCLUDES'

interface TargetComponent {
  id: number
  code: string
  component_sku?: string | null
  image?: {
    src_thumbnail?: string | null
    src_md?: string | null
  } | null
  additional_component_profiles: Array<{
    id: number
    name: string
    description?: string | null
    language: string
  }>
}

interface LinkedComponent {
  id: number
  link_type: string
  display_order: number
  target_component: TargetComponent
}

interface LinkedAdditionalComponentsSectionProps {
  componentId: number
  linkedComponents: LinkedComponent[]
  language: string
  isWrapper: boolean
  onIsWrapperChange: (value: boolean) => void
}

interface LocalLink {
  targetComponentId: number
  targetComponentName: string
  targetComponentImage?: string
  link_type: LinkType
  display_order: number
}

// --- Constants ---

const LINK_TYPE_OPTIONS: LinkType[] = ['INCLUDES', 'RECOMMENDS', 'EXCLUDES']

const LINK_TYPE_LABELS: Record<LinkType, string> = {
  INCLUDES: 'Includes',
  RECOMMENDS: 'Recommends',
  EXCLUDES: 'Excludes',
}

const LINK_TYPE_BADGE_VARIANT: Record<
  LinkType,
  'green' | 'lightBlue' | 'destructive'
> = {
  INCLUDES: 'green',
  RECOMMENDS: 'lightBlue',
  EXCLUDES: 'destructive',
}

// --- Helpers ---

function getComponentName(component: TargetComponent, language: string): string {
  const profiles = component.additional_component_profiles || []
  const profile =
    profiles.find(
      (p) => p.language.toLowerCase() === language.toLowerCase()
    ) || profiles[0]
  return profile?.name || component.code || 'Unknown Component'
}

function getComponentImage(component: TargetComponent): string | undefined {
  return component.image?.src_thumbnail ?? undefined
}

function buildLocalLinks(
  linkedComponents: LinkedComponent[],
  language: string
): LocalLink[] {
  return linkedComponents.map((lc) => ({
    targetComponentId: lc.target_component.id,
    targetComponentName: getComponentName(lc.target_component, language),
    targetComponentImage: getComponentImage(lc.target_component),
    link_type: lc.link_type as LinkType,
    display_order: lc.display_order,
  }))
}

function recalculateDisplayOrders(links: LocalLink[]): LocalLink[] {
  const byType: Record<string, LocalLink[]> = {}
  links.forEach((l) => {
    if (!byType[l.link_type]) byType[l.link_type] = []
    byType[l.link_type].push(l)
  })
  const result: LocalLink[] = []
  Object.values(byType).forEach((group) => {
    group
      .sort((a, b) => a.display_order - b.display_order)
      .forEach((item, i) => {
        result.push({ ...item, display_order: i + 1 })
      })
  })
  return result
}

// --- Component ---

export function LinkedAdditionalComponentsSection({
  componentId,
  linkedComponents,
  language,
  isWrapper,
  onIsWrapperChange,
}: LinkedAdditionalComponentsSectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [localLinks, setLocalLinks] = useState<LocalLink[]>(() =>
    buildLocalLinks(linkedComponents, language)
  )
  const [originalLinks, setOriginalLinks] = useState<LocalLink[]>(() =>
    buildLocalLinks(linkedComponents, language)
  )

  // Search state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLinkType, setSelectedLinkType] = useState<LinkType>('INCLUDES')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Sync with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      const built = buildLocalLinks(linkedComponents, language)
      setLocalLinks(built)
      setOriginalLinks(built)
    }
  }, [linkedComponents, language, isEditing])

  // Apollo
  const [searchComponents, { data: searchData, loading: searchLoading }] =
    useLazyQuery(SEARCH_ADDITIONAL_COMPONENTS, {
      fetchPolicy: 'cache-and-network',
    })

  const [setLinkedComponents, { loading: saving }] = useMutation(
    SET_LINKED_ADDITIONAL_COMPONENTS,
    {
      refetchQueries: ['FindUniqueAdditionalComponent'],
      onCompleted: () => {
        setIsEditing(false)
      },
    }
  )

  // Grouped links
  const groupedLinks = LINK_TYPE_OPTIONS.reduce(
    (acc, type) => {
      acc[type] = localLinks
        .filter((l) => l.link_type === type)
        .sort((a, b) => a.display_order - b.display_order)
      return acc
    },
    {} as Record<LinkType, LocalLink[]>
  )

  // Search results: exclude current component and already-linked components with the same type
  const existingKeys = new Set(
    localLinks.map((l) => `${l.targetComponentId}-${l.link_type}`)
  )
  const searchResults: Array<{
    id: number
    name: string
    image?: string
  }> = (searchData?.findManyAdditionalComponent || [])
    .filter(
      (c: any) =>
        c.id !== componentId &&
        !existingKeys.has(`${c.id}-${selectedLinkType}`)
    )
    .map((c: any) => ({
      id: c.id,
      name: getComponentName(c, language),
      image: getComponentImage(c),
    }))

  // Has changes?
  const hasChanges =
    JSON.stringify(
      localLinks.map((l) => ({
        targetComponentId: l.targetComponentId,
        link_type: l.link_type,
        display_order: l.display_order,
      }))
    ) !==
    JSON.stringify(
      originalLinks.map((l) => ({
        targetComponentId: l.targetComponentId,
        link_type: l.link_type,
        display_order: l.display_order,
      }))
    )

  // --- Handlers ---

  const handleEdit = () => setIsEditing(true)

  const handleCancel = () => {
    setLocalLinks(originalLinks)
    setIsEditing(false)
    setSearchTerm('')
  }

  const handleSave = async () => {
    try {
      await setLinkedComponents({
        variables: {
          componentId,
          links: localLinks.map((l) => ({
            targetComponentId: l.targetComponentId,
            link_type: l.link_type,
            display_order: l.display_order,
          })),
        },
      })
      setOriginalLinks(localLinks)
    } catch (error) {
      console.error('Error saving linked components:', error)
    }
  }

  const handleRemove = (targetComponentId: number, linkType: LinkType) => {
    const updated = localLinks.filter(
      (l) =>
        !(l.targetComponentId === targetComponentId && l.link_type === linkType)
    )
    setLocalLinks(recalculateDisplayOrders(updated))
  }

  const handleReorder = (
    linkType: LinkType,
    index: number,
    direction: 'up' | 'down'
  ) => {
    const group = groupedLinks[linkType]
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === group.length - 1)
    ) {
      return
    }

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const newGroup = group.map((item) => ({ ...item }))
    const temp = newGroup[index]
    newGroup[index] = newGroup[targetIndex]
    newGroup[targetIndex] = temp
    newGroup.forEach((item, i) => {
      item.display_order = i + 1
    })

    const otherLinks = localLinks.filter((l) => l.link_type !== linkType)
    setLocalLinks([...otherLinks, ...newGroup])
  }

  const handleAdd = (componentToAdd: {
    id: number
    name: string
    image?: string
  }) => {
    const group = groupedLinks[selectedLinkType]
    const newLink: LocalLink = {
      targetComponentId: componentToAdd.id,
      targetComponentName: componentToAdd.name,
      targetComponentImage: componentToAdd.image,
      link_type: selectedLinkType,
      display_order: group.length + 1,
    }
    setLocalLinks([...localLinks, newLink])
    setSearchTerm('')
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    if (value.trim().length >= 2) {
      searchComponents({
        variables: {
          where: {
            OR: [
              {
                additional_component_profiles: {
                  some: {
                    name: { contains: value.trim(), mode: 'insensitive' },
                  },
                },
              },
              {
                code: { contains: value.trim(), mode: 'insensitive' },
              },
            ],
          },
          take: 10,
        },
      })
    }
  }

  // --- Render ---

  const hasAnyLinks = localLinks.length > 0
  const nonEmptyGroups = LINK_TYPE_OPTIONS.filter(
    (type) => groupedLinks[type].length > 0
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Linked Components</CardTitle>
        <div className="flex space-x-2">
          {!isEditing && (
            <Button onClick={handleEdit} size="sm" variant="ghost">
              <Edit className="h-4 w-4 mr-2" />
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                onClick={handleCancel}
                size="sm"
                variant="ghost"
                disabled={saving}
              >
                <X className="h-4 w-4 text-red-500" />
                Cancel
              </Button>
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  size="sm"
                  variant="default"
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <Checkbox
            id="is_wrapper"
            checked={isWrapper}
            onCheckedChange={(checked) => onIsWrapperChange(!!checked)}
          />
          <Label htmlFor="is_wrapper">
            Wrapper component (show only linked components)
          </Label>
        </div>
        <div className="space-y-4">
          {/* Read & Edit mode: grouped link list */}
          {hasAnyLinks ? (
            nonEmptyGroups.map((type) => (
              <div key={type}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {LINK_TYPE_LABELS[type]}
                </div>
                <div className="space-y-1">
                  {groupedLinks[type].map((link, index) => (
                    <div
                      key={`${link.link_type}-${link.targetComponentId}`}
                      className="flex items-center justify-between p-2"
                    >
                      <div className="flex items-center space-x-3">
                        {link.targetComponentImage ? (
                          <img
                            src={link.targetComponentImage}
                            alt={link.targetComponentName}
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                            N/A
                          </div>
                        )}
                        <span className="text-sm font-medium">
                          {link.targetComponentName}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={LINK_TYPE_BADGE_VARIANT[type]}>
                          {LINK_TYPE_LABELS[type]}
                        </Badge>
                        {isEditing && (
                          <div className="flex items-center space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleReorder(type, index, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleReorder(type, index, 'down')
                              }
                              disabled={
                                index === groupedLinks[type].length - 1
                              }
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleRemove(link.targetComponentId, type)
                              }
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            !isEditing && (
              <p className="text-gray-500 text-sm">No linked components</p>
            )
          )}

          {/* Edit mode: Add linked component section */}
          {isEditing && (
            <div className="pt-3 border-t">
              <div className="text-xs font-semibold mb-3">
                Add Linked Component
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <div
                    className="flex-1 relative"
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={(e) => {
                      setTimeout(() => {
                        if (
                          !e.currentTarget.contains(document.activeElement)
                        ) {
                          setIsSearchFocused(false)
                        }
                      }, 200)
                    }}
                  >
                    <Input
                      placeholder="Search components by name or code..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    {isSearchFocused && searchTerm.trim().length >= 2 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {searchLoading ? (
                          <div className="p-3 text-sm text-gray-500">
                            Searching...
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map((result) => (
                            <button
                              key={result.id}
                              type="button"
                              className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-muted text-sm"
                              onClick={() => handleAdd(result)}
                            >
                              {result.image ? (
                                <img
                                  src={result.image}
                                  alt={result.name}
                                  className="w-8 h-8 object-cover rounded"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                                  N/A
                                </div>
                              )}
                              <span>{result.name}</span>
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-sm text-gray-500">
                            No components found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <Select
                    value={selectedLinkType}
                    onValueChange={(value) =>
                      setSelectedLinkType(value as LinkType)
                    }
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type} value={type}>
                          {LINK_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
